/**
 * PRF Research — Forage Markets
 *
 * Scores every USDA RMA rainfall-index grid in CONUS on the combined economics of
 * running cattle and capturing PRF subsidy. See docs/prf-research-scope.md.
 *
 * Payload note: prf_grids.json ships grid geometry and rate data once in a shared
 * `grids` array; each segment record references it by index (`i`). Rates are identical
 * across intended use — only County Base Value differs — so this dedupes ~3x.
 */

import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { feature } from "topojson-client";
import { helix } from "ldrs";
import { C } from "./data";
// Deal Underwriter is parked, not removed — same convention as the dormant housing tabs (§4).
// PrfUnderwriter.js and prfUnderwrite.js are untouched; restoring it is uncommenting this
// import and the block at the foot of the right column.
// import PrfUnderwriter from "./PrfUnderwriter";
import ParcelLookup from "./ParcelLookup";
import { THESIS_TILES, THESIS_QUESTION, THESIS_BODY } from "./prfThesis";

// counties-10m carries `states`, `counties` and `nation` objects in one file, and the
// app already fetches it for CountyMap, so it is usually warm in the browser cache.
if (typeof window !== "undefined" && !customElements.get("l-helix")) {
  helix.register();
}

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

const PER = 25; // rankings rows per page

// Deep link: /prf-research?grid=31529. Read once at module scope, the same way App.js reads
// ?county=/?city=, so the value survives any replaceState another mount effect fires before
// the dataset has finished loading and the selection can be applied.
const INITIAL_GRID =
  typeof window === "undefined"
    ? null
    : new URLSearchParams(window.location.search).get("grid");

const SEGMENTS = [
  { id: "grazing", label: "Grazing" },
  { id: "hay_nonirrigated", label: "Hay · non-irrigated" },
  { id: "hay_irrigated", label: "Hay · irrigated" },
];

/* ── Map color ramps ─────────────────────────────────────────────────────
   Two ramps, one system. Generated from the brand navy and validated rather than
   eyeballed (dataviz skill, `scripts/validate_palette.js` helpers):

   · SEQ — magnitude. ONE hue (navy, OKLCH h -104.4), light→dark, lightness strictly
     monotone with min adjacent ΔL 0.118 (floor 0.06). Dark end 15.96:1 on white.
   · DIV — polarity. Two hues with a NEUTRAL GRAY midpoint (chroma 0.004, so zero
     reads as "nothing"), three symmetric steps per arm, poles ΔE 31.6 apart.
     Red = RMA charges more than history; blue = less.

   ⚠️ NO_DATA is warm gray on purpose. The old #e6e9ee sits ΔE 3.2 from SEQ[0] and
   2.2 from the diverging midpoint — under these ramps it read as a low value rather
   than as absent data. #ded9cf clears both (8.2 / 6.6). */
const SEQ = [
  "#eef4fb",
  "#becee3",
  "#91a9c9",
  "#6885ab",
  "#446289",
  "#264164",
  "#0a2240",
];
const DIV = [
  "#d03b3b",
  "#e57770",
  "#f6aba4",
  "#f0efec",
  "#a0c4f4",
  "#689fe6",
  "#2a78d6",
];
const NO_DATA = "#ded9cf";

const mixHex = (a, b, t) => {
  const p = (h, i) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  return `rgb(${[0, 1, 2]
    .map((i) => Math.round(p(a, i) + (p(b, i) - p(a, i)) * t))
    .join(",")})`;
};

// Sample a ramp array at t in [0,1], interpolating between its steps.
const sampleRamp = (steps, t) => {
  const x = Math.max(0, Math.min(1, t)) * (steps.length - 1);
  const i = Math.min(steps.length - 2, Math.floor(x));
  return mixHex(steps[i], steps[i + 1], x - i);
};

/* ── Map layers ───────────────────────────────────────────────────────────
   Everything here already ships in prf_grids.json — nothing new is fetched.

   `get(r, sh)` reads either the per-segment record or the shared grid record.
   `shared: true` marks a layer whose value does not change with intended use — the
   rate surface is identical across all three segments and only CBV differs (§5.4) —
   so the legend can say so rather than implying the map moved when it did not.
   `kind: "div"` uses the diverging ramp, symmetric about zero. */
const LAYERS = [
  // fmtPct/fmtMoney are declared further down; these bodies only run at call time, well
  // after module evaluation, so the forward reference is safe.
  {
    id: "c",
    group: "Core metrics",
    label: "Ground Score",
    get: (r) => r.c,
    fmt: (v) => (v == null ? "—" : v.toFixed(1)),
  },
  {
    id: "y",
    group: "Core metrics",
    label: "SGY",
    get: (r) => r.y,
    fmt: (v) => fmtPct(v, 2),
  },
  {
    id: "sa",
    group: "Core metrics",
    label: "Subsidy $/ac",
    get: (r) => r.sa,
    fmt: (v) => fmtMoney(v, 2),
  },

  ...[
    // ⚠️ Nine dimensions. Rate Mispricing was retired in v3 — see MODEL_PROGRESSION in
    // PrfScoringModel.js. The raw burn-minus-rate overlay below survives it: the figure is
    // still computed and still worth looking at, it just no longer scores.
    ["subsidy_yield", "Subsidy Yield", 25],
    ["forage_productivity", "Forage Productivity", 15],
    ["land_basis", "Land Basis", 17],
    ["hedge_quality", "Hedge Quality", 12],
    ["cbv_boundary", "CBV Boundary Capture", 10],
    ["water_drought", "Water & Drought", 7],
    ["market_access", "Cattle Market Access", 6],
    ["federal_grazing", "Federal Grazing", 5],
    ["operating_reg", "Operating & Regulatory", 3],
  ].map(([key, label, weight]) => ({
    id: `d.${key}`,
    group: "Dimensions",
    label: `${label} · ${weight}%`,
    // Explanations live in App.js's METRIC_INFO and open the app-wide MetricInfoModal —
    // the same modal every thesis tab uses, not an inline expander (§9).
    info: `prf_dim_${key}`,
    // Dimension scores are already 0–100 percentiles, so the domain is fixed rather
    // than calibrated per view — that keeps all nine directly comparable to each other.
    get: (r) => r.d?.[key],
    domain: [0, 100],
    fmt: (v) => (v == null ? "—" : Math.round(v)),
  })),

  {
    id: "mp",
    group: "Program",
    label: "Rate mispricing (burn − rate) · not scored",
    get: (r, sh) => sh?.mp,
    kind: "div",
    shared: true,
    info: "prf_layer_mispricing",
    fmt: (v) =>
      v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)} pp`,
  },
];

const layerById = (id) => LAYERS.find((l) => l.id === id) || LAYERS[0];

// The scorecard's three headline tiles ARE the Core metrics layer group — one definition, so a
// label or format change cannot land on the map and miss the card.
const METRICS = LAYERS.filter((l) => l.group === "Core metrics");

/**
 * The ramp's domain for a layer. Computed once and shared by the map and the legend, so the
 * two cannot drift apart and label a cell with a scale it was not painted from.
 *
 * Three cases:
 *   · a declared domain (dimension percentiles) is used as-is, keeping all ten comparable;
 *   · a diverging layer is forced SYMMETRIC about zero, or the neutral midpoint drifts off
 *     zero and the ramp claims a polarity the data does not have;
 *   · everything else takes the 5th–95th percentile, so outliers cannot flatten the middle of
 *     the ramp where nearly every grid actually sits.
 *
 * Unscored rows are excluded either way, so they cannot drag the endpoints.
 */
const rampDomain = (layer, scored, grids) => {
  if (layer.domain) return { lo: layer.domain[0], hi: layer.domain[1] };
  const vals = scored
    .map((r) => layer.get(r, grids[r.i]))
    .filter((v) => v != null)
    .sort((a, b) => a - b);
  if (!vals.length) return { lo: 0, hi: 1 };
  if (layer.kind === "div") {
    // Symmetric about zero so the neutral midpoint means zero — but the extent comes from the
    // 2nd/98th percentile, not the absolute max. Mispricing runs 80% negative with a long
    // tail; anchoring the arms on the extreme value squeezed almost every grid into the pale
    // center and the map read as "no signal anywhere".
    const p = (q) => Math.abs(vals[Math.floor((vals.length - 1) * q)]);
    const m = Math.max(p(0.02), p(0.98)) || 1;
    return { lo: -m, hi: m };
  }
  return {
    lo: vals[Math.floor(vals.length * 0.05)],
    hi: vals[Math.floor(vals.length * 0.95)],
  };
};

const TIER_COLOR = {
  lead_market: "#1a7a4a",
  priority_market: "#0a2240",
  watchlist: "#7b93ad",
  deprioritized: "#d7dee7",
  insufficient_data: "#eef1f5",
};
/* us-atlas ships bare county names ("Cowlitz", "Acadia", "Virginia Beach"), which read as
   city names on their own. The suffix cannot be a blanket " County" — the dataset carries 49
   Louisiana parishes and two independent cities (51810 Virginia Beach, 32510 Carson City).
   The discriminator is the FIPS county code, not the name: Census reserves 500-999 for
   independent cities, which is why 51036 "Charles City" and 51095 "James City" — both real
   counties at codes 036/095 — correctly keep " County" while 51810 does not. CONUS only, so
   Alaska's boroughs and census areas never arise. */
const countyLabel = (fips, name) => {
  if (!name || !fips) return null;
  const st = fips.slice(0, 2);
  const cty = Number(fips.slice(2));
  if (st === "11") return name; // District of Columbia
  if (cty >= 500) return /\bCity$/.test(name) ? name : `${name} city`;
  if (st === "22") return `${name} Parish`;
  return `${name} County`;
};

const TIER_LABEL = {
  lead_market: "Lead",
  priority_market: "Priority",
  watchlist: "Watchlist",
  deprioritized: "Deprioritized",
  insufficient_data: "No pasture data",
};

/* ── Thesis panel ────────────────────────────────────────────────────────
   Mirrors the Activation/Expansion thesis blocks: four summary tiles that open the
   app-wide MetricInfoModal, then the thesis question and its framing. The what/why/note
   copy lives in METRIC_INFO in App.js (keys below) so it renders in the same modal as
   every other tab. Figures are the measured Phase 0/1 results — scope doc §12–13. */
function ThesisPanel({ accent, onInfo }) {
  return (
    <Card
      sx={{
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${accent}`,
        boxShadow: "none",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.09em",
            color: C.navy,
            textTransform: "uppercase",
            mb: 1.25,
          }}
        >
          Market Intelligence · Forage Markets
        </Typography>

        {/* Tiles open the app-wide MetricInfoModal, same as every other thesis tab */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
            mb: 1.75,
          }}
        >
          {THESIS_TILES.map((t) => (
            <Box
              key={t.key}
              onClick={() => onInfo && onInfo(t.key)}
              sx={{
                border: `1px solid ${accent}22`,
                background: accent + "08",
                borderRadius: 1,
                px: 1.25,
                py: 0.8,
                cursor: onInfo ? "pointer" : "default",
                transition: "border-color .15s",
                "&:hover": { borderColor: accent },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 8.5,
                    fontWeight: 800,
                    letterSpacing: 0.6,
                    color: C.muted,
                    textTransform: "uppercase",
                  }}
                >
                  {t.label}
                </Typography>
                <Typography sx={{ fontSize: 9, color: C.muted }}>
                  &#9432;
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 11.5, fontWeight: 700, color: C.charcoal }}
              >
                {t.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.8,
            color: C.navy,
            textTransform: "uppercase",
            mb: 0.75,
          }}
        >
          Forage Markets Thesis
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: accent, lineHeight: 1.35, mb: 1.5 }}
        >
          {THESIS_QUESTION}
        </Typography>

        {THESIS_BODY.map(([k, v]) => (
          <Typography
            key={k}
            sx={{ fontSize: 10.5, color: C.muted, lineHeight: 1.7, mb: 0.75 }}
          >
            <span style={{ fontWeight: 700, color: C.charcoal }}>{k} — </span>
            {v}
          </Typography>
        ))}
      </CardContent>
    </Card>
  );
}

const DIM_LABEL = {
  subsidy_yield: "Subsidy Yield",
  forage_productivity: "Forage Productivity",
  land_basis: "Land Basis",
  hedge_quality: "Hedge Quality",
  cbv_boundary: "CBV Boundary Capture",
  water_drought: "Water & Drought",
  market_access: "Cattle Market Access",
  federal_grazing: "Federal Grazing",
  operating_reg: "Operating & Regulatory",
};

const WATER_DOCTRINE = {
  1: "prior appropriation",
  2: "hybrid",
  3: "riparian",
};

const STATE_ABBR = {
  "01": "AL",
  "04": "AZ",
  "05": "AR",
  "06": "CA",
  "08": "CO",
  "09": "CT",
  10: "DE",
  12: "FL",
  13: "GA",
  16: "ID",
  17: "IL",
  18: "IN",
  19: "IA",
  20: "KS",
  21: "KY",
  22: "LA",
  23: "ME",
  24: "MD",
  25: "MA",
  26: "MI",
  27: "MN",
  28: "MS",
  29: "MO",
  30: "MT",
  31: "NE",
  32: "NV",
  33: "NH",
  34: "NJ",
  35: "NM",
  36: "NY",
  37: "NC",
  38: "ND",
  39: "OH",
  40: "OK",
  41: "OR",
  42: "PA",
  44: "RI",
  45: "SC",
  46: "SD",
  47: "TN",
  48: "TX",
  49: "UT",
  50: "VT",
  51: "VA",
  53: "WA",
  54: "WV",
  55: "WI",
  56: "WY",
};

const fmtMoney = (v, d = 0) =>
  v == null
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtPct = (v, d = 1) => (v == null ? "—" : `${(v * 100).toFixed(d)}%`);

/* ── Map projection: plain equirectangular so 0.25° cells stay axis-aligned squares ── */
const MAP_W = 900;
const MAP_H = 560;
const LON0 = -125.0;
const LON1 = -66.5;
const LAT0 = 24.0;
const LAT1 = 49.5;
const projX = (lon) => ((lon - LON0) / (LON1 - LON0)) * MAP_W;
const projY = (lat) => ((LAT1 - lat) / (LAT1 - LAT0)) * MAP_H;
const CELL = 0.25;
// Grid id <-> lat/lon, mirroring ground-score-pipeline/collect/gridgeo.py.
// Used to hit-test a click back to a grid without relying on SVG event targeting.
const GRID_LAT0 = 20.125;
const GRID_LON0 = -130.125;
const GRID_STRIDE = 300;

/* ── Boundary layers, projected into our own equirectangular frame ───────
   `feature()` is run once per object and the path strings are memoised: at CONUS zoom
   the county layer is ~3,100 paths and rebuilding it on every pan would stutter. */
function ringsToPath(geometry) {
  const rings = [];
  const walk = (coords, depth) => {
    if (depth === 0) {
      rings.push(coords);
      return;
    }
    coords.forEach((c) => walk(c, depth - 1));
  };
  if (geometry.type === "Polygon") walk(geometry.coordinates, 1);
  else if (geometry.type === "MultiPolygon") walk(geometry.coordinates, 2);

  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  const d = rings
    .map((ring) => {
      const pts = ring.map(([lon, lat]) => {
        const x = projX(lon);
        const y = projY(lat);
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      });
      return "M" + pts.join("L") + "Z";
    })
    .join(" ");
  return { d, bbox: [x0, y0, x1, y1] };
}

/* Bounding boxes are kept alongside the path so the county layer can be viewport-culled.
   Painting all ~3,100 counties on every pan frame is what made zoomed panning sluggish. */
function useBoundaries(topo, objectName) {
  return useMemo(() => {
    if (!topo || !topo.objects?.[objectName]) return [];
    try {
      return feature(topo, topo.objects[objectName]).features.map((f) => {
        const { d, bbox } = ringsToPath(f.geometry);
        return { id: String(f.id), d, bbox };
      });
    } catch {
      return [];
    }
  }, [topo, objectName]);
}

export default function PrfResearch({ dataUrl, onInfo }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [segment, setSegment] = useState("grazing");
  const [colorBy, setColorBy] = useState("c");
  const [sel, setSel] = useState(null);
  // Card-local intended use. The scorecard can be read in a different segment than the map
  // and rankings are set to — the rate surface is identical across all three and only CBV
  // differs (§5.4), so the segment election is often the largest lever on a grid. This
  // deliberately never writes back to `segment`; the map and table keep their own setting.
  const [cardSeg, setCardSeg] = useState("grazing");
  const [stateFilter, setStateFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [topo, setTopo] = useState(null);
  // Bumping this counter tells the map to fly to the current selection. A counter rather
  // than a boolean so repeated clicks on the same row re-trigger the zoom.
  const [zoomTo, setZoomTo] = useState(0);
  const [parcel, setParcel] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportErr, setReportErr] = useState(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    fetch(dataUrl, { cache: "reload" })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        // Behind CloudFront a missing object hits the 403/404 -> /index.html rule and comes
        // back as 200 + text/html, so r.ok is not sufficient. Without this check the failure
        // surfaces as an opaque JSON parse error.
        const ct = r.headers.get("content-type") || "";
        if (ct.includes("text/html")) {
          throw new Error(
            "dataset not found at this path (received the app shell)",
          );
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message));
    fetch(GEO_URL)
      .then((r) => r.json())
      .then(setTopo)
      .catch(() => {});
  }, [dataUrl]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.segments[segment] || [];
  }, [data, segment]);

  // Every segment record keys back to the shared grid array by index (`i`), so the same grid
  // in another intended use is one lookup away. Coverage is not uniform — hay_irrigated
  // carries 12,021 of 13,462 grids — so a segment can legitimately be absent for a grid, and
  // the card surfaces that rather than falling back to another segment's numbers under the
  // wrong label (Tenet 2).
  const byGridIdx = useMemo(() => {
    const out = {};
    if (!data) return out;
    for (const s of SEGMENTS) {
      const m = new Map();
      for (const r of data.segments[s.id] || []) m.set(r.i, r);
      out[s.id] = m;
    }
    return out;
  }, [data]);

  // County names ride along on the topojson the map already fetches — no extra request, and
  // no need to run `feature()` since only the geometry metadata is wanted. Names are bare
  // ("Cowlitz", "Acadia", "Richmond"), so nothing appends " County": Louisiana has parishes
  // and Virginia has independent cities.
  const countyName = useMemo(() => {
    const geoms = topo?.objects?.counties?.geometries;
    if (!geoms) return {};
    const m = {};
    for (const g of geoms) {
      if (g.id != null && g.properties?.name)
        m[String(g.id)] = g.properties.name;
    }
    return m;
  }, [topo]);

  const states = useMemo(() => {
    const s = new Set(rows.map((r) => r.s));
    return [
      "ALL",
      ...[...s].sort((a, b) =>
        (STATE_ABBR[a] || a).localeCompare(STATE_ABBR[b] || b),
      ),
    ];
  }, [rows]);

  // In-state rows regardless of scoring — the map draws these so unscored grids show as
  // "no pasture data" gray instead of leaving holes that read as a broken basemap.
  const inState = useMemo(
    () =>
      stateFilter === "ALL" ? rows : rows.filter((r) => r.s === stateFilter),
    [rows, stateFilter],
  );

  // Scored rows only, ranked — drives the table and the color ramp.
  const filtered = useMemo(
    () => inState.filter((r) => r.c != null).sort((a, b) => b.c - a.c),
    [inState],
  );

  // Two views of the same choice, deliberately out of step.
  //
  // `layer` is the control's own state — the select and its info button track it instantly.
  // `mapLayer` is deferred, so repainting 13,462 cells is treated as the non-urgent update it
  // is. Picking an overlay also opens its modal, and both were urgent state changes landing in
  // one render: React could not paint the modal until the whole canvas had been rebuilt, so
  // the modal visibly waited on the map. Deferring lets the urgent render commit with the map
  // untouched (MapCanvas's memo holds, since mapLayer has not changed yet), then repaints.
  //
  // The map, its legend and its caption all read `mapLayer`, so they stay consistent with each
  // other — the invariant that matters is that the cells and the scale beside them agree, not
  // that the canvas keeps up with the dropdown.
  const layer = layerById(colorBy);
  const deferredLayerId = useDeferredValue(colorBy);
  const mapLayer = layerById(deferredLayerId);
  const legend = useMemo(
    () => rampDomain(mapLayer, filtered, data?.grids || []),
    [mapLayer, filtered, data],
  );

  useEffect(() => setPage(0), [segment, stateFilter]);

  // The card overrides the tab's segment, never the reverse — but when the tab-level control
  // moves, the card follows it, so changing the whole tab to hay does not leave the scorecard
  // silently pinned to grazing.
  useEffect(() => setCardSeg(segment), [segment]);

  // Segment coverage is not uniform, so a card override can be left pointing at a segment the
  // newly selected grid has no record in. Drop back to the tab's segment, which by
  // construction always has one — `sel` is drawn from it.
  useEffect(() => {
    if (!sel || !byGridIdx[cardSeg]) return;
    if (!byGridIdx[cardSeg].has(sel.i)) setCardSeg(segment);
  }, [sel, cardSeg, byGridIdx, segment]);

  // Selecting on the map must surface the row: the table is paginated, so jump to the page
  // holding the selection and scroll it into view. Without this a map click highlighted a
  // row the user could not see.
  useEffect(() => {
    if (!sel) return;
    const idx = filtered.indexOf(sel);
    if (idx < 0) return;
    const target = Math.floor(idx / PER);
    setPage((p) => (p === target ? p : target));
  }, [sel, filtered]);

  // Default selection — the deep-linked grid on first load, otherwise the top-ranked row.
  // `pendingGrid` is one-shot: once consumed (or missed) it clears, so a later selection is
  // never yanked back to the URL's grid when the segment or state filter re-runs this.
  const pendingGrid = useRef(INITIAL_GRID);
  useEffect(() => {
    if (!filtered.length) return;
    if (pendingGrid.current) {
      const want = pendingGrid.current;
      pendingGrid.current = null;
      const hit = filtered.find((r) => data?.grids[r.i]?.g === want);
      if (hit) {
        // Select only — no zoom. A shared link should open on the national view with the grid
        // selected and its scorecard filled in; flying the map on load takes the context away
        // before the reader has seen it. "Zoom map to grid" is right there if they want it.
        setSel(hit);
        return;
      }
    }
    if (!sel || !filtered.includes(sel)) setSel(filtered[0]);
  }, [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selecting a grid anywhere — a rankings row or a cell on the map — brings it into view.
  // The counter is what the map watches; bumping it on every selection means a pick made in
  // the table is never left off-screen, and one made on the map re-centers on what was clicked.
  // useCallback is required, not cosmetic: this is MapCanvas's `onSelect`, and a fresh arrow
  // each render would defeat its React.memo and put all 13,462 rects back on every parent
  // render. Both setters are stable, so the empty dep list is correct.
  const focusGrid = useCallback((r) => {
    setSel(r);
    setZoomTo((n) => n + 1);
  }, []);

  // A resolved parcel drives the selection: its primary grid becomes the selected row and the
  // map flies to it. The parcel's own county is preferred over the grid record's when the grid
  // spans several — the deed's county is the one whose CBV actually applies to it.
  const onParcel = (p) => {
    setParcel(p);
    if (!p || !rows.length) return;
    const want = String(p.primary);
    const inGrid = rows.filter((r) => data?.grids[r.i]?.g === want);
    if (!inGrid.length) return;
    const hit =
      inGrid.find((r) => r.f === p.geoid) ||
      inGrid.find((r) => r.c != null) ||
      inGrid[0];
    // A state filter narrower than the parcel would hide the row the selection points at.
    if (stateFilter !== "ALL" && stateFilter !== hit.s) setStateFilter(hit.s);
    setSel(hit);
    setZoomTo((n) => n + 1);
  };

  /**
   * Build the PDF for the grid currently on the card.
   *
   * Narrative first, then render. If the narrative call fails the report is still produced —
   * every figure in the PDF comes from the dataset, so a missing narrative costs prose, not
   * data, and a reader gets a complete set of numbers either way. The failure is surfaced
   * rather than swallowed so it is obvious the prose is absent by fault, not by design.
   *
   * The imports are dynamic, but note what that does and does not buy: @react-pdf/renderer is
   * ALREADY in the main bundle, because App.js statically imports MarketActionPlan.js which
   * statically imports it. Splitting here defers only this tab's own report code (~4 kB). It
   * becomes a real saving if MarketActionPlan is ever removed or lazy-loaded too.
   */
  const makeReport = async (segRecord, sharedRecord) => {
    setReportBusy(true);
    setReportErr(null);
    try {
      const [{ buildNarrativeRequest, fetchNarrative }, { generateAndOpenPrfReport }] =
        await Promise.all([import("./prfNarrative"), import("./PrfReport")]);

      // Universe and percentile are computed against the CARD's segment, unfiltered — not
      // `filtered`, which is narrowed by the state dropdown. Pairing a rank with the filtered
      // count produced "#857 of 472": two different denominators printed as one ratio.
      const scored = (data.segments[cardSeg] || []).filter((r) => r.c != null);
      const below = scored.filter((r) => r.c < segRecord.c).length;

      const meta = {
        segmentLabel: SEGMENTS.find((x) => x.id === cardSeg)?.label,
        county: countyLabel(segRecord.f, countyName[segRecord.f]),
        state: STATE_ABBR[segRecord.s] || segRecord.s,
        universe: scored.length,
        percentile: scored.length ? (below / scored.length) * 100 : null,
        intervalNames: data.intervals,
        generated: new Date().toISOString().slice(0, 10),
      };

      let narrative = null;
      try {
        const req = buildNarrativeRequest(segRecord, sharedRecord, {
          ...meta,
          parcel: parcel
            ? {
                address: parcel.label,
                acres: Math.round(parcel.acres),
                "grids spanned": parcel.grids.join(", "),
                "county FIPS": parcel.geoid,
              }
            : null,
        });
        narrative = (await fetchNarrative(req)).narrative;
      } catch (e) {
        setReportErr(
          `${e.message || "Narrative unavailable"} — the report was generated without written analysis.`,
        );
      }

      await generateAndOpenPrfReport({
        seg: segRecord,
        shared: sharedRecord,
        meta,
        narrative,
      });
    } catch (e) {
      setReportErr(e?.message || "Could not build the report.");
    } finally {
      setReportBusy(false);
    }
  };

  // Mirror the selection into the URL so a grid can be linked or shared. replaceState, not
  // pushState: a row click is a frequent, low-intent action and stacking one history entry
  // per click would make the back button useless for leaving the tab.
  useEffect(() => {
    if (!data) return;
    const g = sel ? data.grids[sel.i]?.g : null;
    const params = new URLSearchParams(window.location.search);
    if (g) params.set("grid", g);
    else params.delete("grid");
    const qs = params.toString();
    const next = window.location.pathname + (qs ? `?${qs}` : "");
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, "", next);
    }
  }, [sel, data]);

  if (err)
    return (
      <Card
        sx={{
          border: `1px solid ${C.border}`,
          borderTop: `3px solid ${C.red}`,
          boxShadow: "none",
          borderRadius: 1,
        }}
      >
        <CardContent>
          <Typography sx={{ fontSize: 13, color: C.charcoal }}>
            Could not load PRF dataset — <strong>{err}</strong>
            <br />
            <span style={{ color: C.muted }}>
              Run <code>python prf_score.py</code> in{" "}
              <code>ground-score-pipeline/</code> and upload{" "}
              <code>output/prf_grids.json</code> to{" "}
              <code>s3://&lt;bucket&gt;/prf/</code>.
            </span>
          </Typography>
        </CardContent>
      </Card>
    );

  if (!data)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          py: 8,
        }}
      >
        <l-helix size="50" speed="2.5" color={C.navy}></l-helix>
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 600,
            color: C.muted,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Loading PRF grid dataset
        </Typography>
      </Box>
    );

  const grids = data.grids;
  const pageRows = filtered.slice(page * PER, page * PER + PER);
  const selShared = sel ? grids[sel.i] : null;
  const scored = rows.filter((r) => r.c != null).length;

  // The scorecard reads `card`, not `sel`: same grid, but the segment the card is set to.
  // Only the segment record varies — geometry, rates, allocation and index behavior live in
  // `selShared` and are identical across all three intended uses (one rate surface, §5.4).
  const card = sel ? byGridIdx[cardSeg]?.get(sel.i) || null : null;
  // Only consumer was the parked Deal Underwriter — restore alongside it.
  // const cardSegLabel = SEGMENTS.find((s) => s.id === cardSeg)?.label;
  const cardCounty = card ? countyLabel(card.f, countyName[card.f]) : null;
  const overridden = cardSeg !== segment;

  const chip = (active) => ({
    padding: "5px 11px",
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "'Inter',sans-serif",
    border: `1px solid ${active ? C.navy : C.border}`,
    background: active ? C.navy : C.white,
    color: active ? C.white : C.muted,
    cursor: "pointer",
    borderRadius: 3,
  });

  // Card-scale chip, sized to the existing "Zoom map to grid" button so the scorecard keeps
  // one aesthetic rather than growing a second set of controls at tab scale (§9).
  const miniChip = (active, disabled) => ({
    padding: "3px 8px",
    fontSize: 9.5,
    fontWeight: 700,
    fontFamily: "'Inter',sans-serif",
    border: `1px solid ${active ? C.navy : C.border}`,
    background: active ? C.navy : C.white,
    color: disabled ? "#c3ccd7" : active ? C.white : C.muted,
    cursor: disabled ? "not-allowed" : "pointer",
    borderRadius: 3,
    whiteSpace: "nowrap",
  });

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1,
            color: C.muted,
            textTransform: "uppercase",
          }}
        >
          PRF Research · Forage Markets
        </Typography>
      </Box>

      {/* ── Controls ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {SEGMENTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSegment(s.id)}
              style={chip(segment === s.id)}
            >
              {s.label}
            </button>
          ))}
        </Box>
        {/* NB: MUI sx treats a bare `width: 1` as 100%, not 1px */}
        <Box
          sx={{ width: "1px", height: 20, bgcolor: C.border, flexShrink: 0 }}
        />
        {/* Fourteen layers is past what chips can carry, so the picker is a grouped select —
            matching the state filter's existing control rather than inventing a second
            pattern (§9). Every option reads a field already in the payload. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: C.muted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Dimension overlay
          </Typography>
          <Box
            component="select"
            value={colorBy}
            onChange={(e) => {
              const next = layerById(e.target.value);
              setColorBy(next.id);
              // Explain the dimension as it is applied — the overlay is meaningless without
              // knowing the direction, and half of these score inverted.
              if (next.info && onInfo) onInfo(next.info);
            }}
            sx={{
              height: 28,
              boxSizing: "border-box",
              padding: "0 9px",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "'Inter',sans-serif",
              border: `1px solid ${C.border}`,
              // NB: MUI treats a bare number here as theme spacing — keep the px explicit.
              borderRadius: "3px",
              background: C.white,
              color: C.navy,
              cursor: "pointer",
              "&:hover": { borderColor: C.navy },
              "&:focus, &:focus-visible": { outline: "none" },
            }}
          >
            {["Core metrics", "Dimensions", "Program"].map((g) => (
              <optgroup key={g} label={g}>
                {LAYERS.filter((l) => l.group === g).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Box>
          {/* Re-open the explanation without having to re-pick the layer. Square, same 28px
              box as the select beside it, and a drawn icon — the &#9432; glyph rendered at
              whatever weight and baseline the system font felt like. */}
          {layer.info && onInfo && (
            <Box
              component="button"
              onClick={() => onInfo(layer.info)}
              title={`What ${layer.label} measures`}
              sx={{
                height: 28,
                width: 28,
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: `1px solid ${C.border}`,
                background: C.white,
                color: C.muted,
                borderRadius: "3px",
                cursor: "pointer",
                "&:hover": { color: C.navy, borderColor: C.navy },
                "&:focus, &:focus-visible": { outline: "none" },
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 11v5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="7.75" r="1.15" fill="currentColor" />
              </svg>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Two-column body ─────────────────────────────────────────
          Follows the app's market-map convention: 58% left / flex-1 right.
          Left  = map, then rankings.
          Right = thesis, then the selected grid's scorecard.
          Clicking a ranking row updates the scorecard beside it, both in view.
          Stacks to a single column on narrow viewports. */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          mb: 2.5,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            flex: { md: "0 0 58%" },
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Card
            sx={{
              border: `1px solid ${C.border}`,
              borderTop: `3px solid ${C.navy}`,
              boxShadow: "none",
              borderRadius: { xs: 0, md: 1 },
              minWidth: 0,
              // Full-bleed on a phone: cancel the pane's xs gutter so the map gets the whole
              // viewport width, and drop the side borders that would then sit on the screen edge.
              mx: { xs: -1.5, md: 0 },
              borderLeft: { xs: "none", md: `1px solid ${C.border}` },
              borderRight: { xs: "none", md: `1px solid ${C.border}` },
            }}
          >
            <CardContent
              sx={{ p: { xs: 0.75, md: 1.5 }, "&:last-child": { pb: { xs: 0.75, md: 1.5 } } }}
            >
              <MapCanvas
                rows={inState}
                scored={filtered}
                grids={grids}
                layer={mapLayer}
                domain={legend}
                parcel={parcel}
                sel={sel}
                onSelect={focusGrid}
                topo={topo}
                zoomTo={zoomTo}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mt: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    color: C.muted,
                    width: { xs: "100%", md: "50%" },
                    px: { xs: 0.75, md: 0 },
                  }}
                >
                  Each cell is one 0.25° × 0.25° RMA grid (~17 mi), shaded by{" "}
                  {mapLayer.label}
                  {mapLayer.shared
                    ? " — identical across all three intended uses, since only CBV differs by segment"
                    : ""}
                  . Click a cell for its scorecard; drag to pan, +/− to zoom.
                  County lines appear past 2.5× — they matter because CBV is set
                  per county while the rate is set per grid.
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {/* Legend endpoints are the ramp's real domain, not the words low/high —
                      a diverging layer in particular is meaningless without its zero. */}
                  <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                    {mapLayer.fmt(legend.lo)}
                  </Typography>
                  <Box
                    sx={{
                      width: 90,
                      height: 8,
                      borderRadius: 1,
                      background: `linear-gradient(90deg, ${(mapLayer.kind === "div" ? DIV : SEQ).join(", ")})`,
                    }}
                  />
                  <Typography sx={{ fontSize: 9.5, color: C.muted, mr: 1 }}>
                    {mapLayer.fmt(legend.hi)}
                    {mapLayer.kind === "div" ? " · 0 at center" : ""}
                  </Typography>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: 0.5,
                      bgcolor: NO_DATA,
                      border: `1px solid ${C.border}`,
                    }}
                  />
                  <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                    no data
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Parcel lookup sits between the map and the rankings: it is the third way into a
              grid, alongside clicking a cell and clicking a row, and resolving a deed drives
              both of those. Keeping it in this column puts every selection control together. */}
          <ParcelLookup
            parcel={parcel}
            onParcel={onParcel}
            onClear={() => setParcel(null)}
            gridLabel={parcel && selShared ? `grid ${selShared.g}` : null}
          />

          {/* Rankings live under the map in the LEFT column so a row click updates
            the scorecard beside it without scrolling. Full-width previously, which
            left this column empty next to the much taller right column. */}
          <Card
            sx={{
              border: `1px solid ${C.border}`,
              boxShadow: "none",
              borderRadius: 1,
            }}
          >
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
                >
                  Ground Score — {SEGMENTS.find((s) => s.id === segment)?.label}
                </Typography>
                {/* State filter lives here rather than in the top control bar: it scopes the
                    ranking (and the map with it), so it reads as a property of this table. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Typography sx={{ fontSize: 10, color: C.muted }}>
                    {filtered.length.toLocaleString()} of{" "}
                    {grids.length.toLocaleString()} grids ·{" "}
                    {scored.toLocaleString()} scored · RY{" "}
                    {data.generated?.slice(0, 4)} ADM
                  </Typography>
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    aria-label="Filter rankings by state"
                    style={{
                      padding: "3px 6px",
                      fontSize: 10.5,
                      fontFamily: "'Inter',sans-serif",
                      border: `1px solid ${C.border}`,
                      borderRadius: 3,
                      color: C.charcoal,
                      background: C.white,
                    }}
                  >
                    {states.map((st) => (
                      <option key={st} value={st}>
                        {st === "ALL" ? "All states" : STATE_ABBR[st] || st}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>
              <Box sx={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 11,
                    fontFamily: "'Inter',sans-serif",
                  }}
                >
                  <thead>
                    <tr style={{ background: C.rowAlt }}>
                      {[
                        ["#", "left", "6%"],
                        ["Grid", "left", "7%"],
                        ["St", "left", "5%"],
                        ["Score", "right", "7%"],
                        ["SGY", "right", "7%"],
                        ["Sub $/ac", "right", "8%"],
                        ["Rent $/ac", "right", "8%"],
                        ["Land $/ac", "right", "9%"],
                        ["CBV", "right", "8%"],
                        ["Rate", "right", "7%"],
                        ["Counties", "right", "8%"],
                        ["Election", "right", "8%"],
                        ["Tier", "right", "12%"],
                      ].map(([h, align, w]) => (
                        <th
                          key={h}
                          style={{
                            padding: "6px 8px",
                            textAlign: align,
                            width: w,
                            color: C.muted,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, i) => {
                      const sh = grids[r.i];
                      const isSel = sel === r;
                      return (
                        <tr
                          key={`${r.i}-${r.f}`}
                          onClick={() => focusGrid(r)}
                          title="Click to select and bring the grid into view"
                          style={{
                            cursor: "pointer",
                            // Strong enough to find at a glance after a map click, since the
                            // table may have just paged to reach this row.
                            background: isSel
                              ? "#dbe9fb"
                              : i % 2
                                ? C.rowAlt
                                : C.white,
                            borderLeft: isSel
                              ? `3px solid ${C.red}`
                              : "3px solid transparent",
                            outline: isSel
                              ? `1px solid rgba(10,34,64,0.25)`
                              : "none",
                          }}
                        >
                          <td style={{ padding: "5px 8px", color: C.muted }}>
                            {page * PER + i + 1}
                          </td>
                          <td
                            style={{
                              padding: "5px 8px",
                              fontWeight: 600,
                              color: C.charcoal,
                            }}
                          >
                            {sh?.g}
                          </td>
                          <td style={{ padding: "5px 8px", color: C.charcoal }}>
                            {STATE_ABBR[r.s] || r.s}
                          </td>
                          <td
                            style={{
                              padding: "5px 8px",
                              textAlign: "right",
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            {r.c?.toFixed(1)}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            {fmtPct(r.y, 2)}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            {fmtMoney(r.sa, 2)}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            {r.pr == null ? "—" : fmtMoney(r.pr, 2)}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            {fmtMoney(r.pv)}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            {fmtMoney(r.b, 2)}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            {fmtPct(sh?.er, 1)}
                          </td>
                          {/* county span — a grid over >1 county carries a CBV choice (§2.3-A) */}
                          <td
                            style={{
                              padding: "5px 8px",
                              textAlign: "right",
                              color:
                                r.bn > 1 && r.bhi > r.blo ? C.green : C.muted,
                              fontWeight: r.bn > 1 && r.bhi > r.blo ? 700 : 400,
                            }}
                          >
                            {r.bn > 1 && r.bhi > r.blo
                              ? `${r.bn} · +${(((r.bhi - r.blo) / r.blo) * 100).toFixed(0)}%`
                              : r.bn}
                          </td>
                          <td
                            style={{
                              padding: "5px 8px",
                              textAlign: "right",
                              color:
                                (sh?.eu || 0) > 0.001 ? C.charcoal : C.muted,
                            }}
                          >
                            {(sh?.eu || 0) > 0.001
                              ? `+${(sh.eu * 100).toFixed(1)}%`
                              : "—"}
                          </td>
                          <td
                            style={{ padding: "5px 8px", textAlign: "right" }}
                          >
                            <span
                              style={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                color:
                                  r.t === "deprioritized" ? C.muted : C.white,
                                background: TIER_COLOR[r.t],
                                padding: "2px 6px",
                                borderRadius: 2,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {TIER_LABEL[r.t]}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  style={chip(false)}
                >
                  ← Prev
                </button>
                <Typography sx={{ fontSize: 10.5, color: C.muted }}>
                  {page * PER + 1}–{Math.min((page + 1) * PER, filtered.length)}{" "}
                  of {filtered.length.toLocaleString()}
                </Typography>
                <button
                  onClick={() =>
                    setPage((p) =>
                      (p + 1) * PER < filtered.length ? p + 1 : p,
                    )
                  }
                  disabled={(page + 1) * PER >= filtered.length}
                  style={chip(false)}
                >
                  Next →
                </button>
              </Box>
            </CardContent>
          </Card>
        </Box>
        {/* thesis + scorecard share the right column, flex 1 — placed here rather than
            full-width above the map so the thesis copy does not push the map down */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ThesisPanel accent={C.navy} onInfo={onInfo} />
          {sel && selShared && card && (
            <Card
              sx={{
                border: `1px solid ${C.border}`,
                borderTop: `3px solid ${TIER_COLOR[card.t]}`,
                boxShadow: "none",
                borderRadius: 1,
              }}
            >
              <CardContent>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 800, color: C.navy }}
                >
                  Grid {selShared.g} · {cardCounty ? `${cardCounty}, ` : ""}
                  {STATE_ABBR[card.s] || card.s}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.25,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      letterSpacing: "0.06em",
                      color: C.white,
                      bgcolor: C.navy,
                      borderRadius: 3,
                      px: 0.75,
                      py: "2px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    RANK #{card.r?.toLocaleString()}
                  </Box>
                  <Typography sx={{ fontSize: 10.5, color: C.muted }}>
                    {selShared.lat.toFixed(3)}°, {selShared.lon.toFixed(3)}° ·
                    county FIPS {card.f}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <button
                    onClick={() => setZoomTo((n) => n + 1)}
                    style={{ ...miniChip(false, false), color: C.navy }}
                  >
                    Zoom map to grid
                  </button>
                  <button
                    onClick={() => makeReport(card, selShared)}
                    disabled={reportBusy}
                    title="Generate a printable PDF report for this grid"
                    style={{
                      ...miniChip(!reportBusy, reportBusy),
                      color: reportBusy ? "#c3ccd7" : C.white,
                    }}
                  >
                    {reportBusy ? "Building report…" : "Generate report"}
                  </button>
                </Box>
                {reportErr && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: C.charcoal,
                      lineHeight: 1.5,
                      bgcolor: "#fdf4f4",
                      border: "1px solid #f0d5d5",
                      borderRadius: 1,
                      p: 0.75,
                      mb: 1,
                    }}
                  >
                    {reportErr}
                  </Typography>
                )}

                {/* ── Card-local intended use ──────────────────────────────
                    Takes the tab's segment as its starting point and never writes back —
                    the map and rankings keep whatever they were set to. The rate surface
                    is identical across all three intended uses and only CBV differs, so
                    this comparison is often the largest lever on a grid. */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    mb: overridden ? 0.75 : 1.5,
                  }}
                >
                  {SEGMENTS.map((s) => {
                    const has = !!byGridIdx[s.id]?.has(sel.i);
                    return (
                      <button
                        key={s.id}
                        onClick={() => has && setCardSeg(s.id)}
                        disabled={!has}
                        title={
                          has
                            ? `Read this grid as ${s.label}`
                            : `This grid carries no ${s.label} record`
                        }
                        style={miniChip(cardSeg === s.id, !has)}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </Box>
                {overridden && (
                  <Typography sx={{ fontSize: 9.5, color: C.muted, mb: 1.5 }}>
                    Card view only — map and rankings stay on{" "}
                    {SEGMENTS.find((s) => s.id === segment)?.label}.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  {METRICS.map((m) => (
                    <Box
                      key={m.id}
                      sx={{ flex: 1, bgcolor: C.rowAlt, borderRadius: 1, p: 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          color: C.muted,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {m.label}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 15, fontWeight: 800, color: C.navy }}
                      >
                        {m.fmt(m.get(card, selShared))}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* dimensions */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    mb: 0.75,
                  }}
                >
                  Dimensions
                </Typography>
                {Object.entries(card.d || {}).map(([k, v]) => (
                  <Box
                    key={k}
                    onClick={() => onInfo && onInfo(`prf_dim_${k}`)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                      cursor: onInfo ? "pointer" : "default",
                      "&:hover .dimLabel": onInfo
                        ? { color: C.navy, textDecoration: "underline" }
                        : {},
                    }}
                  >
                    {/* Same modal the overlay picker opens — one explanation per dimension,
                        reachable from wherever the reader meets it (§9). */}
                    <Typography
                      className="dimLabel"
                      sx={{
                        fontSize: 10.5,
                        color: C.charcoal,
                        width: 150,
                        flexShrink: 0,
                      }}
                    >
                      {DIM_LABEL[k] || k}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        bgcolor: C.rowAlt,
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${v}%`,
                          height: "100%",
                          bgcolor:
                            v >= 70 ? C.green : v >= 40 ? C.blue : "#b9c5d3",
                          transition: "width .3s",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: C.muted,
                        width: 26,
                        textAlign: "right",
                      }}
                    >
                      {Math.round(v)}
                    </Typography>
                  </Box>
                ))}
                <Typography
                  sx={{ fontSize: 9.5, color: C.muted, mt: 0.5, mb: 2 }}
                >
                  Weight pool {Math.round((card.cv || 0) * 100)}% of all
                  dimensions.
                </Typography>

                {/* policy structure */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    mb: 0.75,
                  }}
                >
                  Optimized policy
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    fontSize: 10.5,
                    borderCollapse: "collapse",
                    mb: 2,
                  }}
                >
                  <tbody>
                    {[
                      ["Coverage level", `${(selShared.cl * 100).toFixed(0)}%`],
                      ["Effective rate", fmtPct(selShared.er, 2)],
                      [
                        "Allocation",
                        (selShared.al || [])
                          .map(
                            ([iv, pct]) =>
                              `${data.intervals[iv]} ${(pct * 100).toFixed(0)}%`,
                          )
                          .join(" · "),
                      ],
                      ["County Base Value", `${fmtMoney(card.b, 2)}/ac`],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: "3px 0", color: C.muted }}>
                          {k}
                        </td>
                        <td
                          style={{
                            padding: "3px 0",
                            textAlign: "right",
                            fontWeight: 600,
                            color: C.charcoal,
                          }}
                        >
                          {v}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>

                {/* index behavior — from the 1948-present RMA series */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    mb: 0.75,
                  }}
                >
                  Index behavior · 1948–present
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    fontSize: 10.5,
                    borderCollapse: "collapse",
                    mb: 2,
                  }}
                >
                  <tbody>
                    {[
                      [
                        "Normal precip, selected intervals",
                        selShared.nm != null ? selShared.nm.toFixed(0) : "—",
                      ],
                      [
                        "Index coefficient of variation",
                        selShared.cvi != null ? selShared.cvi.toFixed(2) : "—",
                      ],
                      [
                        "Trailing-30yr burn cost",
                        selShared.bn_ != null
                          ? `${(selShared.bn_ * 100).toFixed(2)}%`
                          : "—",
                      ],
                      [
                        "Burn − published rate",
                        selShared.mp != null
                          ? `${selShared.mp >= 0 ? "+" : ""}${(selShared.mp * 100).toFixed(2)} pp`
                          : "—",
                      ],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: "3px 0", color: C.muted }}>
                          {k}
                        </td>
                        <td
                          style={{
                            padding: "3px 0",
                            textAlign: "right",
                            fontWeight: 600,
                            color: C.charcoal,
                          }}
                        >
                          {v}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>

                {/* opportunities */}
                {(card.bn > 1 || (selShared.eu || 0) > 0.001) && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: "uppercase",
                        mb: 0.75,
                      }}
                    >
                      Structural opportunities
                    </Typography>
                    {card.bn > 1 && (
                      <Box
                        sx={{
                          bgcolor: "#f2f8f4",
                          border: "1px solid #cfe6d8",
                          borderRadius: 1,
                          p: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: C.charcoal,
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>County-line CBV spread.</strong> This grid
                          spans {card.bn} counties, CBV {fmtMoney(card.blo, 2)}{" "}
                          – {fmtMoney(card.bhi, 2)}/ac
                          {card.bhi > card.blo && (
                            <>
                              {" "}
                              —{" "}
                              <strong>
                                +
                                {(
                                  ((card.bhi - card.blo) / card.blo) *
                                  100
                                ).toFixed(0)}
                                %
                              </strong>{" "}
                              more protection for identical rainfall and an
                              identical rate.
                            </>
                          )}
                        </Typography>
                      </Box>
                    )}
                    {(selShared.eu || 0) > 0.001 && (
                      <Box
                        sx={{
                          bgcolor: "#f5f7fb",
                          border: `1px solid ${C.border}`,
                          borderRadius: 1,
                          p: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: C.charcoal,
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Grid election upside.</strong> A contiguous
                          parcel spanning into a neighboring grid may elect
                          that grid for all its acres:{" "}
                          <strong>+{(selShared.eu * 100).toFixed(1)}%</strong>{" "}
                          rate
                          {card.eg != null && (
                            <> ≈ {fmtMoney(card.eg, 2)}/ac/yr</>
                          )}
                          .
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {/* cautions */}
                {selShared.nm != null && selShared.nm < 300 && (
                  <Box
                    sx={{
                      bgcolor: "#fdf4f4",
                      border: "1px solid #f0d5d5",
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: C.charcoal,
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Low-denominator caution.</strong> Normal
                      precipitation across the selected intervals is only{" "}
                      {selShared.nm.toFixed(0)} (index CV{" "}
                      {selShared.cvi?.toFixed(2)}). Where the normal is near
                      zero the index swings widely in percentage terms without
                      tracking real forage loss — the payout arrives in the
                      wrong years.
                    </Typography>
                  </Box>
                )}

                {/* underlying */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: "uppercase",
                    mt: 1.5,
                    mb: 0.75,
                  }}
                >
                  Land economics
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    fontSize: 10.5,
                    borderCollapse: "collapse",
                  }}
                >
                  <tbody>
                    {[
                      [
                        "Pasture cash rent",
                        card.pr != null ? `${fmtMoney(card.pr, 2)}/ac/yr` : "—",
                      ],
                      [
                        "Pasture value",
                        card.pv == null
                          ? "—"
                          : `${fmtMoney(card.pv)}/ac` +
                            (card.pvs === "state_mean" ? " (state avg)" : ""),
                      ],
                      [
                        "Realized loss ratio (SoB 2010–25)",
                        card.lr != null ? card.lr.toFixed(2) : "—",
                      ],
                      [
                        "Beef cow density",
                        card.bd != null ? `${card.bd.toFixed(1)}/sq mi` : "—",
                      ],
                      [
                        "Nearest feeding center",
                        selShared.fm != null
                          ? `${selShared.fm.toFixed(0)} mi`
                          : "—",
                      ],
                      [
                        "Federal allotment acres nearby",
                        selShared.blm
                          ? `${Number(selShared.blm).toLocaleString()} ac`
                          : "—",
                      ],
                      [
                        "Severe drought (D2+) frequency",
                        card.d2 != null
                          ? `${(card.d2 * 100).toFixed(1)}% of weeks`
                          : "—",
                      ],
                      [
                        "Property tax rate (residential proxy)",
                        card.tx != null ? `${card.tx.toFixed(2)}%` : "—",
                      ],
                      [
                        "Water rights doctrine",
                        card.wd != null ? WATER_DOCTRINE[card.wd] : "—",
                      ],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: "3px 0", color: C.muted }}>
                          {k}
                        </td>
                        <td
                          style={{
                            padding: "3px 0",
                            textAlign: "right",
                            fontWeight: 600,
                            color: C.charcoal,
                          }}
                        >
                          {v}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Deal Underwriter — parked (§4). Underwrites the grid shown in the scorecard
              directly above it, including its segment, so the card's intended use and the
              quoted deal cannot disagree. Uncomment with the import at the top of the file.
          {sel && selShared && card && (
            <PrfUnderwriter
              grid={selShared}
              seg={card}
              intervals={data.intervals}
              segmentLabel={cardSegLabel}
            />
          )} */}
        </Box>
      </Box>

      <Typography
        sx={{ fontSize: 9.5, color: C.muted, mt: 2, lineHeight: 1.6 }}
      >
        <strong>SGY (Subsidized Grazing Yield)</strong> = (pasture cash rent +
        expected PRF subsidy) ÷ pasture value per acre — an annual cash-on-land
        yield. Subsidy assumes a 150% productivity factor and the optimized
        coverage level and interval allocation shown. All 10 dimensions are
        scored; hedge quality is measured from the 1948–present RMA index series
        rather than proxied — where an interval's normal precipitation is near
        zero, the index swings without tracking real forage loss. Internal
        research — not insurance advice; confirm all quotes with a licensed crop
        insurance agent.
      </Typography>
    </Box>
  );
}

/* ── Map canvas: grid cells + state outlines in one shared projection ─────
   ⚠️ Memoised, and that is load-bearing rather than an optimization. At default zoom the
   canvas paints one <rect> per grid — 13,462 of them — and this component sits inside App.js's
   Dashboard. Any Dashboard state change (opening the metric modal, for one) re-rendered the
   whole tree and rebuilt every rect, which is what made the modal take a visible beat to open.
   Every prop is referentially stable across such a render: rows/scored/domain are memoised,
   `layer` is an element of the module-level LAYERS array, and `onSelect` is a useCallback —
   so the memo actually holds. Passing a fresh arrow for onSelect would silently defeat it.

   The declaration below is hoisted, so wrapping it here reads fine. */
const MapCanvas = React.memo(MapCanvasInner);

function MapCanvasInner({
  rows,
  scored,
  grids,
  layer,
  domain,
  sel,
  onSelect,
  topo,
  zoomTo,
  parcel,
}) {
  const FULL = { x: 0, y: 0, w: MAP_W, h: MAP_H };
  const [view, setView] = useState(FULL);
  const svgRef = useRef(null);
  const drag = useRef(null);

  // Parcel rings share the map's projection, so the polygon lands in the same space as the
  // grid cells with no second transform to keep in sync.
  const parcelPath = useMemo(() => {
    const geom = parcel?.geometry;
    if (!geom) return null;
    const rings =
      geom.type === "MultiPolygon"
        ? geom.coordinates.flat()
        : geom.type === "Polygon"
          ? geom.coordinates
          : [];
    return (
      rings
        .map(
          (ring) =>
            "M" +
            ring
              .map(
                ([lon, lat]) =>
                  `${projX(lon).toFixed(2)},${projY(lat).toFixed(2)}`,
              )
              .join("L") +
            "Z",
        )
        .join(" ") || null
    );
  }, [parcel]);

  const states = useBoundaries(topo, "states");
  const counties = useBoundaries(topo, "counties");

  const { lo, hi } = domain;

  const ramp = (v) => {
    if (v == null) return NO_DATA; // absent data — visible, and not a low value
    const t = (v - lo) / (hi - lo || 1);
    return sampleRamp(layer.kind === "div" ? DIV : SEQ, t);
  };

  const cellW = (CELL / (LON1 - LON0)) * MAP_W;
  const cellH = (CELL / (LAT1 - LAT0)) * MAP_H;
  const zoom = MAP_W / view.w; // 1 = whole CONUS

  const clamp = (v) => {
    const w = Math.min(MAP_W, Math.max(MAP_W / 200, v.w));
    const h = w * (MAP_H / MAP_W);
    return {
      w,
      h,
      x: Math.min(Math.max(v.x, -w * 0.15), MAP_W - w * 0.85),
      y: Math.min(Math.max(v.y, -h * 0.15), MAP_H - h * 0.85),
    };
  };

  const zoomBy = (factor, cx, cy) => {
    setView((v) => {
      const w = v.w / factor;
      const h = w * (MAP_H / MAP_W);
      const ax = cx == null ? v.x + v.w / 2 : cx;
      const ay = cy == null ? v.y + v.h / 2 : cy;
      // keep the anchor point stationary on screen
      return clamp({
        w,
        h,
        x: ax - ((ax - v.x) * w) / v.w,
        y: ay - ((ay - v.y) * h) / v.h,
      });
    });
  };

  // grid id -> row, for coordinate hit-testing
  const rowByGid = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const sh = grids[r.i];
      if (sh) m.set(Number(sh.g), r);
    });
    return m;
  }, [rows, grids]);

  const gridAt = (sx, sy) => {
    const lon = LON0 + (sx / MAP_W) * (LON1 - LON0);
    const lat = LAT1 - (sy / MAP_H) * (LAT1 - LAT0);
    const row = Math.round((lat - GRID_LAT0) / CELL);
    const col = Math.round((lon - GRID_LON0) / CELL);
    return rowByGid.get(row * GRID_STRIDE + col) || null;
  };

  const toSvg = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: view.x + ((e.clientX - r.left) / r.width) * view.w,
      y: view.y + ((e.clientY - r.top) / r.height) * view.h,
    };
  };

  // ⚠️ No wheel handler by design. Zooming on scroll meant the map swallowed the wheel over a
  // large part of the page — you could not scroll past it without the view jumping. Zoom is the
  // +/- controls and "US" reset; pan is drag. Both are deliberate actions, which is the right
  // bar for a control that moves the whole view.

  // Pan is computed from the pixel delta against the view captured at pointer-down.
  // Deriving it from the live `view` instead double-counts the origin (the drag start is
  // already an absolute SVG coordinate that includes view.x) and compounds each frame,
  // which made a 20% drag jump the map ~93% of the viewport.
  const onDown = (e) => {
    drag.current = {
      cx: e.clientX,
      cy: e.clientY,
      v: { ...view },
      rect: svgRef.current.getBoundingClientRect(),
      moved: false,
    };
    svgRef.current.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dxPx = e.clientX - d.cx;
    const dyPx = e.clientY - d.cy;
    if (Math.abs(dxPx) > 3 || Math.abs(dyPx) > 3) d.moved = true;
    setView(
      clamp({
        ...d.v,
        x: d.v.x - (dxPx / d.rect.width) * d.v.w,
        y: d.v.y - (dyPx / d.rect.height) * d.v.h,
      }),
    );
  };
  const onUp = (e) => {
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    const d = drag.current;
    drag.current = null;
    // A selection requires a gesture that both started on the map and did not pan. Without
    // the `!d` guard any pointer-up-shaped event reaching this handler hit-tests wherever
    // the cursor happens to be and selects that grid.
    if (!d || d.moved) return;
    const pt = toSvg(e);
    const hit = gridAt(pt.x, pt.y);
    if (hit && hit.c != null) onSelect(hit);
  };

  // ⚠️ pointerleave cancels an in-flight drag and nothing else — it must never select.
  // Wiring it to onUp meant merely moving the cursor off the map ran the hit-test on the
  // exit point and selected whatever grid the cursor left over.
  const onLeave = () => {
    drag.current = null;
  };

  // Bring the selected grid into view — fired by a rankings row, a map cell, a parcel lookup,
  // or "Zoom map to grid".
  //
  // Zoom is only CHANGED when the current view is wider than the default span. Always snapping
  // to a fixed span meant clicking a neighboring cell while zoomed to 128x threw the view back
  // out to 9x, which fights the user. Zoomed out, this zooms in; zoomed in, it is a pure pan.
  useEffect(() => {
    if (!zoomTo || !sel) return;
    const sh = grids[sel.i];
    if (!sh) return;
    const span = cellW * 26; // ~6.5 degrees -> ~9x: the cell reads clearly with 2-4
    // surrounding counties visible, which is the point of flying to it. 16x was too tight
    // to see the county context; 5x left the cell too small to pick out.
    const w = Math.min(view.w, span);
    const h = w * (MAP_H / MAP_W);
    setView(
      clamp({
        w,
        h,
        x: projX(sh.lon) - w / 2,
        y: projY(sh.lat) - h / 2,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomTo]);

  // Cull cells outside the viewport — at high zoom this is the difference between
  // 13.5k rects per frame and a few hundred.
  const visible = useMemo(() => {
    if (zoom < 1.5) return rows;
    const pad = cellW * 2;
    return rows.filter((r) => {
      const sh = grids[r.i];
      if (!sh) return false;
      const x = projX(sh.lon);
      const y = projY(sh.lat);
      return (
        x > view.x - pad &&
        x < view.x + view.w + pad &&
        y > view.y - pad &&
        y < view.y + view.h + pad
      );
    });
  }, [rows, grids, view, zoom, cellW]);

  const showCounties = zoom >= 2.5;
  const selFips = sel?.f;

  const visibleCounties = useMemo(() => {
    if (!showCounties) return [];
    return counties.filter(
      (c) =>
        c.bbox[2] >= view.x &&
        c.bbox[0] <= view.x + view.w &&
        c.bbox[3] >= view.y &&
        c.bbox[1] <= view.y + view.h,
    );
  }, [counties, view, showCounties]);

  const btn = {
    width: 26,
    height: 26,
    fontSize: 13,
    lineHeight: "1",
    fontWeight: 700,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.charcoal,
    cursor: "pointer",
    borderRadius: 3,
    padding: 0,
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        // ⚠️ The 620px floor keeps the CONUS view legible beside the scorecard column, but on
        // a phone it is wider than the viewport and the map overflows its own card. Applied
        // here rather than in the svg's inline style so it can be dropped at the xs breakpoint.
        "& > svg": { minWidth: { xs: 0, md: 620 } },
      }}
    >
      <svg
        ref={svgRef}
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onLeave}
        style={{
          width: "100%",
          display: "block",
          touchAction: "none",
          cursor: drag.current ? "grabbing" : "grab",
          background: "#fbfcfd",
        }}
      >
        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="#fbfcfd"
        />

        {visible.map((r) => {
          const sh = grids[r.i];
          if (!sh) return null;
          const x = projX(sh.lon) - cellW / 2;
          const y = projY(sh.lat) - cellH / 2;
          const isSel = sel === r;
          return (
            <rect
              key={`${r.i}-${r.f}`}
              x={x}
              y={y}
              width={Math.max(cellW, 1.2)}
              height={Math.max(cellH, 1.2)}
              fill={ramp(layer.get(r, sh))}
              stroke={
                isSel ? C.red : zoom >= 5 ? "rgba(10,34,64,0.28)" : undefined
              }
              strokeWidth={isSel ? 3 / zoom : 0.5 / zoom}
              style={{ pointerEvents: "none" }}
            >
              {/* Hover names the layer being shaded, so a value read off the map is never
                  ambiguous about which of the fourteen it belongs to. */}
              <title>
                {`Grid ${sh.g} · ${STATE_ABBR[r.s] || r.s} — ` +
                  (r.c == null
                    ? "no pasture data"
                    : `${layer.label}: ${layer.fmt(layer.get(r, sh))}`)}
              </title>
            </rect>
          );
        })}

        {/* County lines matter here specifically: County Base Value is set per COUNTY while
            the rate is set per GRID, so a county line crossing a cell is the CBV-capture
            opportunity made visible (scope doc §2.3-A). Only drawn once zoomed in. */}
        {showCounties &&
          visibleCounties.map((c) => (
            <path
              key={c.id}
              d={c.d}
              fill="none"
              stroke={
                c.id === selFips
                  ? "rgba(217,45,32,0.95)"
                  : "rgba(10,34,64,0.22)"
              }
              strokeWidth={(c.id === selFips ? 2.4 : 0.95) / zoom}
              strokeDasharray={
                c.id === selFips ? `${5 / zoom} ${3 / zoom}` : undefined
              }
            />
          ))}

        {states.map((st) => (
          <path
            key={st.id}
            d={st.d}
            fill="none"
            stroke="rgba(10,34,64,0.5)"
            strokeWidth={1.4 / zoom}
          />
        ))}

        {/* Looked-up parcel, drawn last so it sits above cells and boundaries. A real deed is
            tiny against a 17-mile cell, so it carries a halo stroke to stay findable at low
            zoom — without it the parcel vanishes until you are already zoomed in. */}
        {parcelPath && (
          <g style={{ pointerEvents: "none" }}>
            <path
              d={parcelPath}
              fill="none"
              stroke="rgba(255,255,255,0.9)"
              strokeWidth={4 / zoom}
              strokeLinejoin="round"
            />
            <path
              d={parcelPath}
              fill="rgba(217,45,32,0.28)"
              stroke="#d92d20"
              strokeWidth={1.6 / zoom}
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>

      {/* controls */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <button style={btn} onClick={() => zoomBy(1.6)} title="Zoom in">
          +
        </button>
        <button style={btn} onClick={() => zoomBy(1 / 1.6)} title="Zoom out">
          −
        </button>
        <button
          style={{ ...btn, fontSize: 9, fontWeight: 700 }}
          onClick={() => setView(FULL)}
          title="Reset to full US"
        >
          US
        </button>
      </Box>

      {zoom > 1.05 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            bgcolor: "rgba(255,255,255,0.9)",
            border: `1px solid ${C.border}`,
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 9.5, color: C.muted }}>
            {zoom.toFixed(1)}× ·{" "}
            {showCounties ? "county lines on" : "zoom in for county lines"}
            {showCounties && sel ? (
              <>
                {" · "}
                <span style={{ color: "rgb(214,140,17)", fontWeight: 700 }}>
                  ▭ county of record
                </span>
                {" · "}
                <span style={{ color: C.red, fontWeight: 700 }}>
                  ▭ selected grid
                </span>
              </>
            ) : null}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
